import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { useAbility } from '../../context/AbilityContext.js';
import { FALLBACK_ROLE_OPTIONS, type RoleOption } from '../../hooks/useRoleOptions.js';
import { ApiError, api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

import {
  RoleSupplierField,
  SUPPLIER_REQUIRED_MESSAGE,
  roleEffectLine,
  useSupplierChoices,
} from './RoleSupplierField.js';
import { RoleControls, SUPERADMIN, breaksRule, splitHeld } from './roleCard.js';

export interface EditableUser {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  /** 073: `null` means the account holds NO role — which is not the same as
   * holding Customer. Conflating the two is what stopped this form ever sending
   * `role: 'Customer'` for a role-less account. */
  role?: string | null;
  /** 332 (FR-007): the roles the user actually holds, by name (`GET /users/:id` `roles`). When given, the form shows
   * them and takes its current value from them, never from an Admin/Customer/none reduction. */
  roles?: string[];
  /** 333 (FR-005): the roles this viewer may grant (`GET /users/:id` `assignable_roles`). Absent (an older backend)
   * means Customer and Admin. */
  assignable_roles?: string[];
  /** 376: organisation roles held through live supplier memberships; they count toward "one staff role". */
  membership_roles?: string[];
  /** 367 (FR-001, FR-002): every role, with whether this viewer may give it and why not (`GET /users/:id`). */
  role_options?: RoleOption[];
}

/** An older backend without `role_options`: its `assignable_roles` as givable platform roles. */
function optionsOf(user: EditableUser): RoleOption[] {
  if (user.role_options) return user.role_options;
  const names = user.assignable_roles ?? FALLBACK_ROLE_OPTIONS.grantable;
  return names.map((name) => ({ name, kind: 'platform', grantable: true, reason: null }));
}

/** Admin edit-user form (035 US3). Name/phone/email/role + a Send-reset action
 * (the admin never sets a password). Renders nothing unless can:manage User. */
export function UserEditForm({ user }: { user: EditableUser }) {
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ?? '');
  // 332: the roles actually held. 376 (#1262): split into the one staff role and Customer; an account from before
  // 376's clean-up may hold two staff roles, and is shown so (it is fixed by saving a choice).
  const held = user.roles ?? (user.role ? [user.role] : []);
  const initial = splitHeld(held);
  // A role held only through a membership (e.g. Admin on the account, Host at a supplier) breaks the rule too.
  const viaMembership = (user.membership_roles ?? []).filter((r) => !held.includes(r));
  const conflict = breaksRule([...held, ...viaMembership]);
  const initialStaff = initial.staff.length === 1 ? (initial.staff[0] as string) : '';
  const [staffRole, setStaffRole] = useState<string>(initialStaff);
  const [customer, setCustomer] = useState<boolean>(initial.customer);
  // 333/334/367: every role the platform lists, greyed where this viewer may not give it.
  const options = optionsOf(user);
  const chosen = staffRole !== initialStaff ? options.find((o) => o.name === staffRole) : undefined;
  // 376: an organisation role they already hold through a membership needs no new supplier (the platform keeps it).
  const isOrganisationRole =
    chosen?.kind === 'organisation' && !(user.membership_roles ?? []).includes(staffRole);
  const [supplierId, setSupplierId] = useState('');
  const suppliers = useSupplierChoices(canManage && isOrganisationRole);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/users/${user._id}`, body),
    onSuccess: () => {
      setError('');
      setNotice('Changes saved.');
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.one(user._id) });
      // 161: found by T026's sweep — the record was refreshed but the Users list was not, so it
      // kept the old name and role until a reload.
      queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.list });
      queryClient.invalidateQueries({ queryKey: ['user-events', user._id] });
      // 367: an organisation role adds a supplier membership.
      queryClient.invalidateQueries({ queryKey: ['user-memberships', user._id] });
    },
    onError: (err: unknown) => {
      setNotice('');
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'Failed to save');
    },
  });

  const sendReset = useMutation({
    mutationFn: () => api.post(`/users/${user._id}/password-reset`),
    onSuccess: () => {
      setError('');
      setNotice('Password-reset email sent.');
      queryClient.invalidateQueries({ queryKey: ['user-events', user._id] });
    },
    onError: (err: unknown) => {
      setNotice('');
      setError(err instanceof Error ? err.message : 'Failed to send reset');
    },
  });

  if (!canManage) return null;

  // 367 (design 1c, 1e): what saving the chosen role means.
  const effect = roleEffectLine({
    personName: [firstName, lastName].filter(Boolean).join(' '),
    role: chosen,
    supplierName: suppliers.find((x) => x._id === supplierId)?.company_name ?? null,
  });

  function onSave() {
    const body: Record<string, unknown> = {};
    if (firstName !== user.first_name) body.first_name = firstName;
    if (lastName !== user.last_name) body.last_name = lastName;
    if (phone !== (user.phone ?? '')) body.phone = phone;
    if (email !== user.email) body.email = email;
    // 376: the account's roles, sent together whenever either control (or a two-staff-role account) changes.
    const effectiveCustomer = staffRole === SUPERADMIN ? false : customer;
    if (conflict || staffRole !== initialStaff || effectiveCustomer !== initial.customer) {
      if (!staffRole && !effectiveCustomer) {
        setNotice('');
        setError('Choose a staff role, or tick Also a customer.');
        return;
      }
      body.staff_role = staffRole || null;
      body.customer = effectiveCustomer;
    }
    if (isOrganisationRole) {
      if (!supplierId) {
        setNotice('');
        setError(SUPPLIER_REQUIRED_MESSAGE);
        return;
      }
      body.supplier_id = supplierId;
    }
    if (Object.keys(body).length === 0) {
      setError('No changes to save.');
      return;
    }
    save.mutate(body);
  }

  return (
    <Card>
      <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">Edit user</h2>

      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="First name" name="edit_first_name">
            <Input
              id="edit_first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.currentTarget.value)}
            />
          </FormField>
          <FormField label="Last name" name="edit_last_name">
            <Input
              id="edit_last_name"
              value={lastName}
              onChange={(e) => setLastName(e.currentTarget.value)}
            />
          </FormField>
        </div>

        <FormField label="Email" name="edit_email" hint="Changing this re-triggers verification">
          <Input
            id="edit_email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </FormField>

        <FormField label="Phone" name="edit_phone">
          <Input id="edit_phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
        </FormField>

        <div className="grid gap-2 border-t border-hairline pt-4">
          <h3 className="text-sm font-semibold text-ink">Role</h3>
          {conflict && (
            <p
              data-testid="role-conflict"
              className="rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
            >
              {viaMembership.length > 0
                ? `This account holds ${held.join(', ') || 'no role'}, and ${viaMembership.join(', ')} through a supplier membership: one staff role per account. Choose ${viaMembership.join(' or ')} and save, or remove the membership below.`
                : `This account holds ${held.join(', ')}: one staff role per account. Choose one and save.`}
            </p>
          )}
          <RoleControls
            idPrefix="edit"
            staffRole={staffRole}
            customer={customer}
            onStaffRole={setStaffRole}
            onCustomer={setCustomer}
            options={options}
            dropsCustomer={initial.customer}
          />
        </div>
        {isOrganisationRole && (
          <RoleSupplierField
            id="edit_role_supplier"
            roleName={staffRole}
            value={supplierId}
            onChange={setSupplierId}
            suppliers={suppliers}
          />
        )}
        {effect && (
          <p
            data-testid="role-effect"
            className="-mt-2 rounded-md bg-brand-blue-soft px-3 py-2 text-sm text-ink"
          >
            {effect}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button loading={save.isPending} onClick={onSave}>
            Save changes
          </Button>
          <Button
            variant="secondary"
            loading={sendReset.isPending}
            onClick={() => sendReset.mutate()}
          >
            Send password reset
          </Button>
        </div>
        {notice && <p className="text-sm text-green-600">{notice}</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Card>
  );
}
